import type { AutomationTestType } from '@tms/shared';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { useCallback, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { Button, Field, Input, Select, Textarea } from '../../../components/ui';
import { useErrorText, useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { AutomationError } from '../client';
import type { ProjectInput } from '../hooks';

/**
 * Pieces the team and project pages share: error toasts, the segmented
 * switch, the project form and the «type the name to delete» zone.
 */

/* ─── errors ─── */

/** An error's text; TestRun out of reach gets its own words (`automation.unreachable`). */
export const useAutomationErrorText = () => {
  const { t } = useT();
  const { errorText } = useErrorText();
  return useCallback(
    (error: unknown): string =>
      error instanceof AutomationError && error.status === 0
        ? t('automation.unreachable')
        : errorText(error),
    [t, errorText],
  );
};

/** A failed call as a toast. */
export const useFailToast = () => {
  const text = useAutomationErrorText();
  return useCallback((error: unknown) => void toast.error(text(error)), [text]);
};

/* ─── segmented switch ─── */

/** Mutually exclusive buttons: a status filter, a view, a mode. */
export const Segmented = <T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (value: T) => void;
  label: string;
}) => (
  <div
    role="group"
    aria-label={label}
    className="inline-flex flex-wrap gap-0.5 rounded-lg bg-sunken p-0.5"
  >
    {options.map((option) => (
      <button
        key={option.value}
        type="button"
        aria-pressed={option.value === value}
        onClick={() => onChange(option.value)}
        className={cn(
          'rounded-md px-2.5 py-1 text-[12px] font-medium transition-colors',
          option.value === value
            ? 'bg-surface text-ink shadow-sm'
            : 'text-ink-muted hover:text-ink',
        )}
      >
        {option.label}
      </button>
    ))}
  </div>
);

/* ─── the project form ─── */

export interface ProjectDraft {
  name: string;
  description: string;
  note: string;
  gitlabProjectId: string;
  gitlabPipelineUrl: string;
  jenkinsUrl: string;
  testType: AutomationTestType;
}

export const emptyProjectDraft = (): ProjectDraft => ({
  name: '',
  description: '',
  note: '',
  gitlabProjectId: '',
  gitlabPipelineUrl: '',
  jenkinsUrl: '',
  testType: 'API',
});

/** A project needs its name, its GitLab project and where its pipelines are. */
export const projectDraftValid = (draft: ProjectDraft): boolean =>
  Boolean(draft.name.trim() && draft.gitlabProjectId.trim() && draft.gitlabPipelineUrl.trim());

/** A new project's fields; empty optional ones are left out. */
export const newProjectInput = (draft: ProjectDraft): ProjectInput => ({
  name: draft.name.trim(),
  description: draft.description,
  gitlabProjectId: draft.gitlabProjectId.trim(),
  gitlabPipelineUrl: draft.gitlabPipelineUrl.trim(),
  testType: draft.testType,
  ...(draft.jenkinsUrl.trim() ? { jenkinsUrl: draft.jenkinsUrl.trim() } : {}),
  ...(draft.note.trim() ? { note: draft.note.trim() } : {}),
});

export const TestTypeSelect = ({
  id,
  value,
  onChange,
  className,
  ...rest
}: {
  id?: string;
  value: AutomationTestType;
  onChange: (value: AutomationTestType) => void;
  className?: string;
  'aria-label'?: string;
}) => (
  <Select
    id={id}
    value={value}
    onChange={(event) => onChange(event.target.value as AutomationTestType)}
    className={className}
    {...rest}
  >
    <option value="API">API</option>
    <option value="UI">UI</option>
  </Select>
);

/** Name, type, description, note, GitLab and Jenkins — adding a project and its settings. */
export const ProjectForm = ({
  draft,
  onChange,
  withHints = false,
  children,
}: {
  draft: ProjectDraft;
  onChange: (patch: Partial<ProjectDraft>) => void;
  /** the settings explain what the GitLab ID is for */
  withHints?: boolean;
  /** more fields at the end */
  children?: ReactNode;
}) => {
  const { t } = useT();
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-[1fr_10rem]">
        <Field label={t('automation.project.field.name')} required>
          {({ id }) => (
            <Input
              id={id}
              value={draft.name}
              placeholder={t('automation.project.field.namePlaceholder')}
              onChange={(event) => onChange({ name: event.target.value })}
            />
          )}
        </Field>
        <Field label={t('automation.project.field.testType')}>
          {({ id }) => (
            <TestTypeSelect
              id={id}
              value={draft.testType}
              onChange={(testType) => onChange({ testType })}
            />
          )}
        </Field>
      </div>
      <Field label={t('common.description')}>
        {({ id }) => (
          <Textarea
            id={id}
            rows={2}
            value={draft.description}
            placeholder={t('automation.project.field.descriptionPlaceholder')}
            onChange={(event) => onChange({ description: event.target.value })}
          />
        )}
      </Field>
      <Field
        label={t('automation.project.field.note')}
        hint={t('automation.project.field.noteHint')}
      >
        {({ id, describedBy }) => (
          <Textarea
            id={id}
            rows={2}
            aria-describedby={describedBy}
            value={draft.note}
            placeholder={t('automation.project.field.notePlaceholder')}
            onChange={(event) => onChange({ note: event.target.value })}
          />
        )}
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label={t('automation.project.field.gitlabProjectId')}
          hint={withHints ? t('automation.project.field.gitlabProjectIdHint') : undefined}
          required
        >
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={draft.gitlabProjectId}
              placeholder="9210"
              onChange={(event) => onChange({ gitlabProjectId: event.target.value })}
            />
          )}
        </Field>
        <Field label={t('automation.project.field.jenkinsUrl')}>
          {({ id }) => (
            <Input
              id={id}
              value={draft.jenkinsUrl}
              placeholder="https://jenkins…/job/…"
              onChange={(event) => onChange({ jenkinsUrl: event.target.value })}
            />
          )}
        </Field>
      </div>
      <Field label={t('automation.project.field.pipelineUrl')} required>
        {({ id }) => (
          <Input
            id={id}
            value={draft.gitlabPipelineUrl}
            placeholder="https://gitlab…/qa/loan/-/pipelines"
            onChange={(event) => onChange({ gitlabPipelineUrl: event.target.value })}
          />
        )}
      </Field>
      {children}
    </div>
  );
};

/* ─── deleting ─── */

/**
 * Deleting a team or a project: first what goes with it, then the name typed
 * in — a deletion takes runs and test data along and cannot be undone.
 */
export const DangerZone = ({
  title,
  text,
  deleteLabel,
  name,
  impact,
  loading,
  onDelete,
}: {
  title: string;
  text: string;
  deleteLabel: string;
  /** what has to be typed */
  name: string;
  /** rendered only once deleting is asked for, so a count can load then */
  impact: ReactNode;
  loading: boolean;
  onDelete: () => void;
}) => {
  const { t } = useT();
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');

  return (
    <section className="rounded-lg border border-fail/40 p-4">
      <h3 className="mb-2 flex items-center gap-2 text-[14px] font-semibold text-ink">
        <AlertTriangle className="size-4 text-fail" aria-hidden="true" />
        {title}
      </h3>
      {!confirming ? (
        <>
          <p className="mb-3 text-[13px] text-ink-muted">{text}</p>
          <Button
            variant="secondary"
            size="sm"
            className="text-fail"
            onClick={() => setConfirming(true)}
          >
            <Trash2 className="size-3.5" />
            {deleteLabel}
          </Button>
        </>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="text-[13px] text-ink-muted">{impact}</div>
          <Field label={t('automation.project.danger.typeToConfirm', { name })}>
            {({ id }) => (
              <Input
                id={id}
                value={typed}
                placeholder={name}
                autoFocus
                onChange={(event) => setTyped(event.target.value)}
              />
            )}
          </Field>
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              size="sm"
              onClick={() => {
                setConfirming(false);
                setTyped('');
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              size="sm"
              disabled={typed.trim() !== name}
              loading={loading}
              onClick={onDelete}
            >
              <Trash2 className="size-3.5" />
              {t('automation.project.danger.deleteForever')}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
};

/** Names typed with commas → a list. */
export const splitNames = (text: string): string[] =>
  text
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean);
